/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Entity, CompoundEntityRef } from '@backstage/catalog-model';
import { Config } from '@backstage/config';
import { ForwardedError, toError } from '@backstage/errors';

// Maximum size in bytes for a single upload part (5MB)
const MAX_SINGLE_UPLOAD_BYTES = 5 * 1024 * 1024;

import {
  AwsCredentialsManager,
  DefaultAwsCredentialsManager,
} from '@backstage/integration-aws-node';
import {
  GetObjectCommand,
  CopyObjectCommand,
  DeleteObjectCommand,
  HeadBucketCommand,
  HeadObjectCommand,
  PutObjectCommand,
  PutObjectCommandInput,
  ListObjectsV2CommandOutput,
  ListObjectsV2Command,
  S3Client,
  S3ServiceException,
} from '@aws-sdk/client-s3';
import { fromTemporaryCredentials } from '@aws-sdk/credential-providers';
import { NodeHttpHandler } from '@smithy/node-http-handler';
import { Upload } from '@aws-sdk/lib-storage';
import { AwsCredentialIdentityProvider } from '@aws-sdk/types';
import { HttpsProxyAgent } from 'hpagent';
import express from 'express';
import fs from 'fs-extra';
import JSON5 from 'json5';
import createLimiter from 'p-limit';
import path from 'node:path';
import { Readable } from 'node:stream';
import {
  bulkStorageOperation,
  getCloudPathForLocalPath,
  getFileTreeRecursively,
  getHeadersForFileExtension,
  getStaleFiles,
  isValidContentPath,
  lowerCaseEntityTriplet,
  lowerCaseEntityTripletInStoragePath,
  normalizeExternalStorageRootPath,
} from './helpers';
import {
  PublisherBase,
  PublishRequest,
  PublishResponse,
  ReadinessResponse,
  TechDocsMetadata,
} from './types';
import { LoggerService } from '@backstage/backend-plugin-api';
import { AwsS3Integration, ScmIntegrations } from '@backstage/integration';

const streamToBuffer = (stream: Readable): Promise<Buffer> => {
  return new Promise((resolve, reject) => {
    try {
      const chunks: any[] = [];
      stream.on('data', chunk => chunks.push(chunk));
      stream.on('error', (e: Error) =>
        reject(new ForwardedError('Unable to read stream', e)),
      );
      stream.on('end', () => resolve(Buffer.concat(chunks)));
    } catch (e) {
      throw new ForwardedError('Unable to parse the response data', e);
    }
  });
};

export class AwsS3Publish implements PublisherBase {
  public readonly storageClient: S3Client;
  private readonly bucketName: string;
  private readonly legacyPathCasing: boolean;
  private readonly logger: LoggerService;
  private readonly bucketRootPath: string;
  private readonly sse?: 'aws:kms' | 'AES256';
  private readonly maxAttempts: number;

  constructor(options: {
    storageClient: S3Client;
    bucketName: string;
    legacyPathCasing: boolean;
    logger: LoggerService;
    bucketRootPath: string;
    sse?: 'aws:kms' | 'AES256';
    maxAttempts: number;
  }) {
    this.storageClient = options.storageClient;
    this.bucketName = options.bucketName;
    this.legacyPathCasing = options.legacyPathCasing;
    this.logger = options.logger;
    this.bucketRootPath = options.bucketRootPath;
    this.sse = options.sse;
    this.maxAttempts = options.maxAttempts;
  }

  static async fromConfig(
    config: Config,
    logger: LoggerService,
  ): Promise<PublisherBase> {
    let bucketName = '';
    try {
      bucketName = config.getString('techdocs.publisher.awsS3.bucketName');
    } catch (error) {
      throw new Error(
        "Since techdocs.publisher.type is set to 'awsS3' in your app config, " +
          'techdocs.publisher.awsS3.bucketName is required.',
      );
    }

    const bucketRootPath = normalizeExternalStorageRootPath(
      config.getOptionalString('techdocs.publisher.awsS3.bucketRootPath') || '',
    );

    const sse = config.getOptionalString('techdocs.publisher.awsS3.sse') as
      | 'aws:kms'
      | 'AES256'
      | undefined;

    // AWS Region is an optional config. If missing, default AWS env variable AWS_REGION
    // or AWS shared credentials file at ~/.aws/credentials will be used.
    const region = config.getOptionalString('techdocs.publisher.awsS3.region');

    // Credentials can optionally be configured by specifying the AWS account ID, which will retrieve credentials
    // for the account from the 'aws' section of the app config.
    // Credentials can also optionally be directly configured in the techdocs awsS3 config, but this method is
    // deprecated.
    // If no credentials are configured, the AWS SDK V3's default credential chain will be used.
    const accountId = config.getOptionalString(
      'techdocs.publisher.awsS3.accountId',
    );
    const credentialsConfig = config.getOptionalConfig(
      'techdocs.publisher.awsS3.credentials',
    );

    const credsManager = DefaultAwsCredentialsManager.fromConfig(config);

    const scmIntegrations = ScmIntegrations.fromConfig(config);
    const awsS3Integrations = scmIntegrations.awsS3.list();

    const sdkCredentialProvider = await AwsS3Publish.buildCredentials(
      credsManager,
      logger,
      awsS3Integrations,
      accountId,
      credentialsConfig,
      region,
    );

    // AWS endpoint is an optional config. If missing, the default endpoint is built from
    // the configured region.
    const endpoint = config.getOptionalString(
      'techdocs.publisher.awsS3.endpoint',
    );

    // AWS HTTPS proxy is an optional config. If missing, no proxy is used
    const httpsProxy = config.getOptionalString(
      'techdocs.publisher.awsS3.httpsProxy',
    );

    // AWS forcePathStyle is an optional config. If missing, it defaults to false. Needs to be enabled for cases
    // where endpoint url points to locally hosted S3 compatible storage like Localstack
    const forcePathStyle = config.getOptionalBoolean(
      'techdocs.publisher.awsS3.s3ForcePathStyle',
    );

    // AWS MAX ATTEMPTS is an optional config. If missing, default value of 5 is used
    const maxAttempts = config.getOptionalNumber(
      'techdocs.publisher.awsS3.maxAttempts',
    );

    const storageClient = new S3Client({
      customUserAgent: 'backstage-aws-techdocs-s3-publisher',
      credentialDefaultProvider: () => sdkCredentialProvider,
      ...(region && { region }),
      ...(endpoint && { endpoint }),
      ...(forcePathStyle && { forcePathStyle }),
      // Enhanced retry configuration for better reliability
      maxAttempts: maxAttempts || 5,
      retryMode: 'adaptive',
      // Enhanced connection settings for large file uploads
      requestHandler: new NodeHttpHandler({
        ...(httpsProxy && {
          httpsAgent: new HttpsProxyAgent({ proxy: httpsProxy }),
        }),
        connectionTimeout: 60000,
        socketTimeout: 120000,
      }),
    });

    const legacyPathCasing =
      config.getOptionalBoolean(
        'techdocs.legacyUseCaseSensitiveTripletPaths',
      ) || false;

    return new AwsS3Publish({
      storageClient,
      bucketName,
      bucketRootPath,
      legacyPathCasing,
      logger,
      sse,
      maxAttempts: maxAttempts || 5,
    });
  }

  private static buildStaticCredentials(
    accessKeyId: string,
    secretAccessKey: string,
  ): AwsCredentialIdentityProvider {
    return async () => {
      return Promise.resolve({
        accessKeyId,
        secretAccessKey,
      });
    };
  }

  private static async buildCredentials(
    credsManager: AwsCredentialsManager,
    logger: LoggerService,
    awsS3Integrations: AwsS3Integration[],
    accountId?: string,
    credentialsConfig?: Config,
    region?: string,
  ): Promise<AwsCredentialIdentityProvider> {
    // Pull credentials for the specified account ID from the 'aws' config section
    if (accountId) {
      return (await credsManager.getCredentialProvider({ accountId }))
        .sdkCredentialProvider;
    }

    const explicitCredentials = await AwsS3Publish.getExplicitCredentials({
      credsManager,
      credentialsConfig,
      awsS3Integrations,
      logger,
    });

    const roleArn = credentialsConfig?.getOptionalString('roleArn');
    if (roleArn) {
      return fromTemporaryCredentials({
        masterCredentials: explicitCredentials,
        params: {
          RoleSessionName: 'backstage-aws-techdocs-s3-publisher',
          RoleArn: roleArn,
        },
        clientConfig: { region },
      });
    }

    return explicitCredentials;
  }
  /**
   * Custom retry wrapper for S3 operations with detailed error handling.
   */
  public async retryOperation<TOutput>(
    operation: () => Promise<TOutput>,
    operationName: string,
    maxAttempts: number = 3,
    shouldRetry: (
      error: S3ServiceException,
    ) => boolean = this.defaultShouldRetry.bind(this),
  ): Promise<TOutput> {
    for (let attempt = 1; attempt < maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        const e = error as S3ServiceException;
        if (!shouldRetry(e)) {
          this.logger.error(`${operationName} failed: ${e.message}`);
          throw e;
        }

        this.logger.warn(`${operationName} failed, retrying...`, {
          attempt,
          maxAttempts,
          error: e.message,
          errorCode: e.name,
          httpStatusCode: e.$metadata?.httpStatusCode,
        });

        // Enhanced exponential backoff with jitter
        const baseDelay = operationName.startsWith('Upload-') ? 2000 : 1000;
        const backoffDelay = Math.min(
          baseDelay * Math.pow(2, attempt - 1),
          30000,
        );
        const jitter = Math.random() * 1000;
        await new Promise(resolve =>
          setTimeout(resolve, backoffDelay + jitter),
        );
      }
    }
    return await operation();
  }

  /**
   * Determines if an S3 operation should be retried based on the error details.
   */
  private defaultShouldRetry(error: S3ServiceException): boolean {
    const httpStatusCode = error.$metadata?.httpStatusCode;
    const errorCode = error.name;

    // Truly transient errors that should always be retried
    const transientErrors = [
      'NetworkingError',
      'TimeoutError',
      'ConnectionError',
      'RequestTimeout',
      'ServiceUnavailable',
      'SlowDown',
      'ThrottlingException',
    ];

    // Server errors are always considered transient
    if (httpStatusCode && httpStatusCode >= 500) {
      return true;
    }

    // Specific 4xx errors that are known to be transient
    if (httpStatusCode && httpStatusCode >= 400 && httpStatusCode < 500) {
      const retriable4xxErrors = [
        'RequestTimeout',
        'RequestTimeoutException',
        'PriorRequestNotComplete',
      ];
      return retriable4xxErrors.includes(errorCode);
    }

    // Check against known transient errors
    return transientErrors.some(
      retriableError =>
        errorCode === retriableError || error.message.includes(retriableError),
    );
  }

  private static async getExplicitCredentials({
    credentialsConfig,
    awsS3Integrations,
    credsManager,
    logger,
  }: {
    credentialsConfig?: Config;
    awsS3Integrations: AwsS3Integration[];
    credsManager: AwsCredentialsManager;
    logger: LoggerService;
  }): Promise<AwsCredentialIdentityProvider> {
    const accessKeyId = credentialsConfig?.getOptionalString('accessKeyId');
    const secretAccessKey =
      credentialsConfig?.getOptionalString('secretAccessKey');

    if (accessKeyId && secretAccessKey) {
      return AwsS3Publish.buildStaticCredentials(accessKeyId, secretAccessKey);
    }

    if (awsS3Integrations.length > 0) {
      if (awsS3Integrations.length === 1) {
        const singleAwsS3IntegrationConfig = awsS3Integrations[0].config;

        const singleAwsS3IntegrationAccessKeyId =
          singleAwsS3IntegrationConfig.accessKeyId;

        const singleAwsS3IntegrationSecretAccessKey =
          singleAwsS3IntegrationConfig.secretAccessKey;

        if (
          singleAwsS3IntegrationAccessKeyId &&
          singleAwsS3IntegrationSecretAccessKey
        ) {
          return AwsS3Publish.buildStaticCredentials(
            singleAwsS3IntegrationAccessKeyId,
            singleAwsS3IntegrationSecretAccessKey,
          );
        }
      } else {
        if (accessKeyId) {
          const targetAwsS3IntegrationConfig = awsS3Integrations.find(
            c => c.config.accessKeyId === accessKeyId,
          );

          if (!targetAwsS3IntegrationConfig) {
            logger.warn(
              `No AWS S3 integration config under integrations.awsS3 found for access key id ${accessKeyId}.`,
            );
          }
          const targetAwsS3IntegrationAccessKeyId =
            targetAwsS3IntegrationConfig?.config.accessKeyId;
          const targetAwsS3IntegrationSecretAccessKey =
            targetAwsS3IntegrationConfig?.config.secretAccessKey;
          if (
            targetAwsS3IntegrationAccessKeyId &&
            targetAwsS3IntegrationSecretAccessKey
          ) {
            return AwsS3Publish.buildStaticCredentials(
              targetAwsS3IntegrationAccessKeyId,
              targetAwsS3IntegrationSecretAccessKey,
            );
          }
        }
      }
    }
    return (await credsManager.getCredentialProvider()).sdkCredentialProvider;
  }

  /**
   * Check if the defined bucket exists. Being able to connect means the configuration is good
   * and the storage client will work.
   */
  async getReadiness(): Promise<ReadinessResponse> {
    try {
      await this.storageClient.send(
        new HeadBucketCommand({ Bucket: this.bucketName }),
      );

      this.logger.info(
        `Successfully connected to the AWS S3 bucket ${this.bucketName}.`,
      );

      return { isAvailable: true };
    } catch (error) {
      this.logger.error(
        `Could not retrieve metadata about the AWS S3 bucket ${this.bucketName}. ` +
          'Make sure the bucket exists. Also make sure that authentication is setup either by ' +
          'explicitly defining credentials and region in techdocs.publisher.awsS3 in app config or ' +
          'by using environment variables. Refer to https://backstage.io/docs/features/techdocs/using-cloud-storage',
      );
      this.logger.error(
        `from AWS client library`,
        error instanceof Error ? error : new Error(String(error)),
      );
      return {
        isAvailable: false,
      };
    }
  }
  /**
   * Upload all the files from the generated `directory` to the S3 bucket.
   * Directory structure used in the bucket is - entityNamespace/entityKind/entityName/index.html
   */
  async publish({
    entity,
    directory,
  }: PublishRequest): Promise<PublishResponse> {
    const objects: string[] = [];
    const useLegacyPathCasing = this.legacyPathCasing;
    const bucketRootPath = this.bucketRootPath;
    const sse = this.sse;

    // Track timing for performance monitoring
    const publishStartTime = Date.now();

    // First, try to retrieve a list of all individual files currently existing
    let existingFiles: string[] = [];
    try {
      const remoteFolder = getCloudPathForLocalPath(
        entity,
        undefined,
        useLegacyPathCasing,
        bucketRootPath,
      );
      const response = await this.retryOperation(
        async () => {
          const listCommand = new ListObjectsV2Command({
            Bucket: this.bucketName,
            Prefix: remoteFolder,
          });
          return this.storageClient.send(listCommand);
        },
        'ListObjects',
        this.maxAttempts,
      );
      existingFiles = (response.Contents || [])
        .map(f => f.Key || '')
        .filter(f => !!f);
    } catch (e) {
      this.logger.error(
        `Unable to list files for Entity ${entity.metadata.name}: ${
          toError(e).message
        }`,
      );
    }

    // Then, merge new files into the same folder
    let absoluteFilesToUpload;
    try {
      // Remove the absolute path prefix of the source directory
      // Path of all files to upload, relative to the root of the source directory
      // e.g. ['index.html', 'sub-page/index.html', 'assets/images/favicon.png']
      absoluteFilesToUpload = await getFileTreeRecursively(directory);

      await bulkStorageOperation(
        async absoluteFilePath => {
          const relativeFilePath = path.relative(directory, absoluteFilePath);
          const s3Key = getCloudPathForLocalPath(
            entity,
            relativeFilePath,
            useLegacyPathCasing,
            bucketRootPath,
          );
          // Create params without the Body because the body must be the
          // actual file contents (Buffer or Readable), not the path string.
          // For multipart uploads we attach a Readable stream to avoid
          // buffering large files in memory. For simple uploads we attach
          // a Buffer read from disk.
          const params: PutObjectCommandInput = {
            Bucket: this.bucketName,
            Key: s3Key,
            ...(sse && { ServerSideEncryption: sse }),
          };

          objects.push(params.Key!);
          // Get file stats before upload
          const stats = await fs.stat(absoluteFilePath);
          const fileSizeInBytes = stats.size;

          // Check if this is a large file that requires multipart upload
          if (fileSizeInBytes >= MAX_SINGLE_UPLOAD_BYTES) {
            // Try multipart upload for large files
            try {
              // Create stream and Upload inside retry closure so stream is
              // recreated on each retry attempt (streams are consumable once).
              await this.retryOperation(
                () => {
                  // Create a fresh stream on each attempt
                  const fileStream = fs.createReadStream(absoluteFilePath);
                  const uploadParams = { ...params, Body: fileStream };

                  const upload = new Upload({
                    client: this.storageClient,
                    params: uploadParams,
                    partSize: MAX_SINGLE_UPLOAD_BYTES,
                    queueSize: 3,
                    leavePartsOnError: false,
                  });
                  return upload.done();
                },
                `Upload-${params.Key}`,
                this.maxAttempts,
              );
              return;
            } catch (multipartError) {
              const s3Error = multipartError as any;
              const errorName = s3Error?.name || 'Unknown';

              // For specific multipart errors, attempt simple upload fallback
              if (errorName === 'InvalidPart' || errorName === 'NoSuchUpload') {
                this.logger.warn(
                  `Multipart upload failed for ${params.Key}, attempting simple upload fallback.`,
                );
              } else {
                // Non-recoverable multipart error, throw it
                this.logger.error(
                  `Multipart upload failed for ${params.Key}: ${
                    multipartError instanceof Error
                      ? multipartError.message
                      : String(multipartError)
                  }`,
                );
                throw multipartError;
              }
            }
          }

          // Use simple upload for small files or as fallback from multipart
          try {
            const fileContent = await fs.readFile(absoluteFilePath);
            const putParams = { ...params, Body: fileContent };
            await this.retryOperation(
              () => this.storageClient.send(new PutObjectCommand(putParams)),
              `Upload-${params.Key}`,
              this.maxAttempts,
            );

            if (fileSizeInBytes >= MAX_SINGLE_UPLOAD_BYTES) {
              this.logger.info(
                `Simple upload fallback succeeded for ${params.Key}`,
              );
            }
          } catch (error) {
            this.logger.error(
              `Upload failed for ${params.Key}: ${
                error instanceof Error ? error.message : String(error)
              }`,
            );
            throw error;
          }
        },
        absoluteFilesToUpload,
        { concurrencyLimit: 10 },
      );

      this.logger.info(
        `Successfully uploaded all the generated files for Entity ${entity.metadata.name}. Total number of files: ${absoluteFilesToUpload.length}`,
      );
    } catch (e) {
      const errorMessage = `Unable to upload file(s) to AWS S3. ${e}`;
      this.logger.error(errorMessage);
      throw new Error(errorMessage);
    }

    // Last, try to remove the files that were *only* present previously
    try {
      const relativeFilesToUpload = absoluteFilesToUpload.map(
        absoluteFilePath =>
          getCloudPathForLocalPath(
            entity,
            path.relative(directory, absoluteFilePath),
            useLegacyPathCasing,
            bucketRootPath,
          ),
      );
      const staleFiles = getStaleFiles(relativeFilesToUpload, existingFiles);

      await bulkStorageOperation(
        async relativeFilePath => {
          return this.retryOperation(
            async () => {
              const deleteCommand = new DeleteObjectCommand({
                Bucket: this.bucketName,
                Key: relativeFilePath,
              });
              return this.storageClient.send(deleteCommand);
            },
            'DeleteObject',
            this.maxAttempts,
          );
        },
        staleFiles,
        { concurrencyLimit: 10 },
      );
      this.logger.info(
        `Successfully deleted stale files for Entity ${entity.metadata.name}. Total number of files: ${staleFiles.length}`,
      );
    } catch (error) {
      const errorMessage = `Unable to delete file(s) from AWS S3. ${error}`;
      this.logger.error(errorMessage);
    }
    const publishEndTime = Date.now();
    const publishDurationMs = publishEndTime - publishStartTime;
    this.logger.info(
      `Successfully published ${objects.length} files for ${
        entity.metadata.name
      } in ${Math.round(publishDurationMs / 1000)}s`,
    );
    return { objects };
  }

  async fetchTechDocsMetadata(
    entityName: CompoundEntityRef,
  ): Promise<TechDocsMetadata> {
    try {
      return await new Promise<TechDocsMetadata>(async (resolve, reject) => {
        const entityTriplet = `${entityName.namespace}/${entityName.kind}/${entityName.name}`;
        const entityDir = this.legacyPathCasing
          ? entityTriplet
          : lowerCaseEntityTriplet(entityTriplet);

        const entityRootDir = path.posix.join(this.bucketRootPath, entityDir);
        if (!isValidContentPath(this.bucketRootPath, entityRootDir)) {
          this.logger.error(
            `Invalid content path found while fetching TechDocs metadata: ${entityRootDir}`,
          );
          throw new Error(`Metadata Not Found`);
        }

        try {
          const resp = await this.retryOperation(
            async () => {
              const getCommand = new GetObjectCommand({
                Bucket: this.bucketName,
                Key: `${entityRootDir}/techdocs_metadata.json`,
              });
              return this.storageClient.send(getCommand);
            },
            'GetTechDocsMetadata',
            this.maxAttempts,
          );

          const techdocsMetadataJson = await streamToBuffer(
            resp.Body as Readable,
          );
          if (!techdocsMetadataJson) {
            throw new Error(
              `Unable to parse the techdocs metadata file ${entityRootDir}/techdocs_metadata.json.`,
            );
          }

          const techdocsMetadata = JSON5.parse(
            techdocsMetadataJson.toString('utf-8'),
          );

          resolve(techdocsMetadata);
        } catch (err) {
          this.logger.error(toError(err).message);
          reject(new Error(toError(err).message));
        }
      });
    } catch (e) {
      throw new ForwardedError('TechDocs metadata fetch failed', e);
    }
  }

  /**
   * Express route middleware to serve static files on a route in techdocs-backend.
   */
  docsRouter(): express.Handler {
    return async (req, res) => {
      const decodedUri = decodeURI(req.path.replace(/^\//, ''));

      // filePath example - /default/component/documented-component/index.html
      const filePathNoRoot = this.legacyPathCasing
        ? decodedUri
        : lowerCaseEntityTripletInStoragePath(decodedUri);

      // Prepend the root path to the relative file path
      const filePath = path.posix.join(this.bucketRootPath, filePathNoRoot);
      if (!isValidContentPath(this.bucketRootPath, filePath)) {
        this.logger.error(
          `Attempted to fetch TechDocs content for a file outside of the bucket root: ${filePathNoRoot}`,
        );
        res.status(404).send('File Not Found');
        return;
      }

      // Files with different extensions (CSS, HTML) need to be served with different headers
      const fileExtension = path.extname(filePath);
      const responseHeaders = getHeadersForFileExtension(fileExtension);

      try {
        const resp = await this.storageClient.send(
          new GetObjectCommand({ Bucket: this.bucketName, Key: filePath }),
        );

        // Inject response headers
        for (const [headerKey, headerValue] of Object.entries(
          responseHeaders,
        )) {
          res.setHeader(headerKey, headerValue);
        }

        (resp.Body as Readable)
          .on('error', err => {
            this.logger.warn(
              `TechDocs S3 router failed to serve static files from bucket ${this.bucketName} at key ${filePath}: ${err.message}`,
            );
            if (!res.headersSent) {
              res.status(404).send('File Not Found');
            } else {
              res.destroy();
            }
          })
          .pipe(res);
      } catch (err) {
        this.logger.warn(
          `TechDocs S3 router failed to serve static files from bucket ${
            this.bucketName
          } at key ${filePath}: ${toError(err).message}`,
        );
        res.status(404).send('File Not Found');
      }
    };
  }

  /**
   * A helper function which checks if index.html of an Entity's docs site is available. This
   * can be used to verify if there are any pre-generated docs available to serve.
   */
  async hasDocsBeenGenerated(entity: Entity): Promise<boolean> {
    try {
      const entityTriplet = `${entity.metadata.namespace}/${entity.kind}/${entity.metadata.name}`;
      const entityDir = this.legacyPathCasing
        ? entityTriplet
        : lowerCaseEntityTriplet(entityTriplet);

      const entityRootDir = path.posix.join(this.bucketRootPath, entityDir);
      if (!isValidContentPath(this.bucketRootPath, entityRootDir)) {
        this.logger.error(
          `Invalid content path found while checking if docs have been generated: ${entityRootDir}`,
        );
        return Promise.resolve(false);
      }

      await this.storageClient.send(
        new HeadObjectCommand({
          Bucket: this.bucketName,
          Key: `${entityRootDir}/index.html`,
        }),
      );
      return Promise.resolve(true);
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  async migrateDocsCase({
    removeOriginal = false,
    concurrency = 25,
  }): Promise<void> {
    // Iterate through every file in the root of the publisher.
    const allObjects = await this.getAllObjectsFromBucket();
    const limiter = createLimiter(concurrency);
    await Promise.all(
      allObjects.map(f =>
        limiter(async file => {
          let newPath;
          try {
            newPath = lowerCaseEntityTripletInStoragePath(file);
          } catch (e) {
            this.logger.warn(toError(e).message);
            return;
          }

          // If all parts are already lowercase, ignore.
          if (file === newPath) {
            return;
          }

          try {
            this.logger.debug(`Migrating ${file}`);
            await this.storageClient.send(
              new CopyObjectCommand({
                Bucket: this.bucketName,
                CopySource: [this.bucketName, file].join('/'),
                Key: newPath,
              }),
            );

            if (removeOriginal) {
              await this.storageClient.send(
                new DeleteObjectCommand({
                  Bucket: this.bucketName,
                  Key: file,
                }),
              );
            }
          } catch (e) {
            this.logger.warn(
              `Unable to migrate ${file}: ${toError(e).message}`,
            );
          }
        }, f),
      ),
    );
  }

  /**
   * Returns a list of all object keys from the configured bucket.
   */
  protected async getAllObjectsFromBucket(
    { prefix } = { prefix: '' },
  ): Promise<string[]> {
    const objects: string[] = [];
    let nextContinuation: string | undefined;
    let allObjects: ListObjectsV2CommandOutput;
    // Iterate through every file in the root of the publisher.
    do {
      const currentToken = nextContinuation;
      allObjects = await this.retryOperation(
        async () => {
          const listCommand = new ListObjectsV2Command({
            Bucket: this.bucketName,
            ContinuationToken: currentToken,
            ...(prefix ? { Prefix: prefix } : {}),
          });
          return this.storageClient.send(listCommand);
        },
        'GetAllObjects',
        this.maxAttempts,
      );
      objects.push(
        ...(allObjects.Contents || []).map(f => f.Key || '').filter(f => !!f),
      );
      nextContinuation = allObjects.NextContinuationToken;
    } while (nextContinuation);

    return objects;
  }
}
